import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface FormSectionProps {
  title?: string;
  description?: string;
  columns?: 1 | 2;
  children: ReactNode;
  className?: string;
  /**
   * `card` (default) — standalone card with its own border + shadow. Used when
   * a form has a single section or sections that should read as separate
   * surfaces.
   * `nested` — chromeless section intended to sit inside a parent card
   * together with other nested sections (divided by `divide-y` on the parent).
   * The title still anchors the section but there's no border or shadow.
   */
  variant?: 'card' | 'nested';
}

export function FormSection({
  title,
  description,
  columns = 2,
  children,
  className,
  variant = 'card',
}: FormSectionProps) {
  if (variant === 'nested') {
    return (
      <section className={cn('px-6 py-6', className)}>
        {title ? (
          <header className="mb-5 space-y-1">
            <h3 className="font-display text-title-md text-accent-ink leading-tight">{title}</h3>
            {description ? (
              <p className="text-body-sm leading-relaxed text-neutral-500">{description}</p>
            ) : null}
          </header>
        ) : null}
        <div
          className={cn(
            'grid items-start gap-5',
            columns === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1',
          )}
        >
          {children}
        </div>
      </section>
    );
  }

  return (
    <section
      className={cn('bg-surface rounded-xl border border-(--color-border) shadow-xs', className)}
    >
      {title ? (
        <header className="space-y-1 border-b border-(--color-border) px-6 py-4">
          <h3 className="font-display text-title-md text-accent-ink leading-tight">{title}</h3>
          {description ? (
            <p className="text-body-sm leading-relaxed text-neutral-500">{description}</p>
          ) : null}
        </header>
      ) : null}
      <div
        className={cn(
          'grid items-start gap-5 p-6',
          columns === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1',
        )}
      >
        {children}
      </div>
    </section>
  );
}
